import React from "react";
import { Box, Container, MantineSize, useMantineTheme } from "@mantine/core";
import AppShell from "./AppShell";
import SiteFooter from "./SiteFooter";
import { MeshGradientBackground } from "../BackgroundLayers";

type PageVariant = "hero" | "section" | "content";

interface PageShellProps {
  children: React.ReactNode;
  variant?: PageVariant;
  showMeshBackground?: boolean;
  showAppShell?: boolean;
  /** Extra animated background layers (rendered under MeshGradientBackground). */
  extraBackgrounds?: React.ReactNode;
  /** Override the variant's default Container size — use sparingly. */
  containerSize?: MantineSize;
  /** Render the in-flow site footer at the bottom of the page. Default true. */
  showFooter?: boolean;
}

/**
 * PageShell - Layout template shared across every page.
 *
 * Bakes in AppShell + background gradient + MeshGradientBackground +
 * Container with variant-appropriate spacing. Pages render just their
 * inner Stack + content.
 *
 * Variants encode the page's visual tempo:
 * - `hero`    Home, About — hero-first pages with heavy content mass at the top.
 *             Comfortable `py` sized against the hero's own visual weight.
 * - `section` Dance, Media, Tech — section-index pages whose first element is
 *             usually a bare Heading1. Extra top breathing so the H1 doesn't
 *             feel jammed against the AppShell edge.
 * - `content` Contact, Think/Index, Think/Show, Media/Show — text-narrow pages
 *             where `size="lg"` and moderate padding read as calmer.
 */
const VARIANT_CONFIG: Record<
  PageVariant,
  { size: MantineSize; py: { base: number; sm: number; md: number } }
> = {
  hero: {
    size: "xl",
    py: { base: 72, sm: 80, md: 80 },
  },
  section: {
    size: "xl",
    py: { base: 80, sm: 104, md: 128 },
  },
  content: {
    size: "lg",
    py: { base: 80, sm: 80, md: 96 },
  },
};

export const PageShell: React.FC<PageShellProps> = ({
  children,
  variant = "section",
  showMeshBackground = true,
  showAppShell = true,
  extraBackgrounds,
  containerSize,
  showFooter = true,
}) => {
  const theme = useMantineTheme();
  const other = theme.other as Record<string, string>;
  const config = VARIANT_CONFIG[variant];
  const size = containerSize ?? config.size;

  const content = (
    <Box
      style={{
        position: "relative",
        minHeight: "100vh",
        background: other.backgroundColor,
      }}
    >
      {extraBackgrounds}
      {showMeshBackground && <MeshGradientBackground />}
      <Container
        size={size}
        py={config.py}
        style={{ position: "relative", zIndex: 2 }}
      >
        {children}
      </Container>
      {showFooter && <SiteFooter />}
    </Box>
  );

  return showAppShell ? <AppShell>{content}</AppShell> : content;
};

export default PageShell;
